import { StyledReactSelect } from '@fountain/fountain-ui-components';
import { Grid } from '@material-ui/core';
import {
  WorkflowAccurateStage,
  WorkflowBackgroundCheckerStage,
  WorkflowSterlingStage,
} from 'api-clients/monolith';
import { CheckrGeo } from 'api-clients/monolith/models/CheckrGeo';
import { NodeOption } from 'api-clients/monolith/models/NodeOption';
import { PackageOption } from 'api-clients/monolith/models/PackageOption';
import produce from 'immer';
import React, { useContext, VFC } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { REACT_APP_MONOLITH_BASE_URL } from 'runtimeEnvVars';

import { StageContext } from 'containers/WorkflowEditor/contexts/stageContext';

import { StageSettingCard } from '../../../../StageSettingCard';
import { SettingSwitch } from '../../../SettingSwitch';
import {
  AutoAdvanceWhenLabelsComplete,
  IsHiddenFromPortal,
} from '../../SettingsSwitches';
import { messages } from './messages';
import { useStyles } from './styles';

export interface IntegrationCardProps {
  stage:
    | WorkflowAccurateStage
    | WorkflowBackgroundCheckerStage
    | WorkflowSterlingStage;
}

export const IntegrationCard: VFC<IntegrationCardProps> = ({ stage }) => {
  const styles = useStyles();
  const intl = useIntl();
  const { accountSlug } = useParams<{
    accountSlug: string;
  }>();

  const { setStage, updateStageResult } = useContext(StageContext);

  const errors =
    (updateStageResult?.isError && updateStageResult.error?.errors) ||
    undefined;

  const onPackageChange = (packageOption: PackageOption) => {
    const { value } = packageOption;

    setStage(
      produce(stage, draftStage => {
        if (draftStage.type === 'SterlingStage') {
          draftStage.additional_info.package_id = value;
        } else {
          draftStage.additional_info.package_name = value;
        }

        if (draftStage.type === 'BackgroundCheckerStage') {
          if (packageOption.supports_mvr_first) {
            draftStage.additional_info.mvr_first = true;
          } else {
            draftStage.additional_info.mvr_first = false;
          }
        }
      }),
    );
  };
  const hasStageLabels = stage.has_stage_labels;

  const onCheckrGeoChange = (geoOption: CheckrGeo) => {
    setStage(
      produce(stage, draftStage => {
        if (draftStage.type !== 'BackgroundCheckerStage') {
          return;
        }
        draftStage.additional_info.checkr_geo = geoOption;
        draftStage.additional_info.checkr_geo_id = geoOption.checkr_geo_id;
        draftStage.additional_info.checkr_geo_name = geoOption.checkr_geo_name;
      }),
    );
  };

  const onToggleMvrFirst = () => {
    setStage(
      produce(stage, draftStage => {
        if (draftStage.type !== 'BackgroundCheckerStage') {
          return;
        }
        draftStage.additional_info.mvr_first =
          !draftStage.additional_info.mvr_first;
      }),
    );
  };

  const onNodeChange = (nodeOption: NodeOption) => {
    setStage(
      produce(stage, draftStage => {
        if (draftStage.type !== 'BackgroundCheckerStage') {
          return;
        }

        draftStage.additional_info.node_id = nodeOption.value;
        draftStage.additional_info.package_name = null;
        draftStage.additional_info.mvr_first = false;
      }),
    );
  };

  const hasNodeOptions =
    stage.type === 'BackgroundCheckerStage' &&
    stage.additional_info.node_options &&
    stage.additional_info.node_options.length > 0;

  const selectedNodeId =
    stage.type === 'BackgroundCheckerStage'
      ? stage.additional_info.node_id
      : null;

  const nodeOptions =
    stage.type === 'BackgroundCheckerStage'
      ? (stage.additional_info.node_options ?? [])
      : [];

  const currentNodeSelection =
    stage.type === 'BackgroundCheckerStage'
      ? (stage.additional_info.node_options?.find(
          (node: NodeOption) => node.value === selectedNodeId,
        ) ?? null)
      : null;

  // Filter packages based on node selection
  // If node has no packages, fall back to all account packages
  const availablePackages =
    currentNodeSelection && currentNodeSelection.packages.length > 0
      ? currentNodeSelection.packages
      : (stage.additional_info.package_options ?? []);

  const packageName =
    stage.type === 'AccurateStage' || stage.type === 'BackgroundCheckerStage'
      ? stage.additional_info.package_name
      : stage.additional_info.package_id;

  const selectedPackage =
    availablePackages?.find(option => option.value === packageName) ?? null;

  // Disable package selector if node options exist but no node is selected
  const isPackageSelectorDisabled = hasNodeOptions && !currentNodeSelection;

  const cardTitleByStageType: Record<
    'BackgroundCheckerStage' | 'AccurateStage' | 'SterlingStage' | string,
    string
  > = {
    AccurateStage: intl.formatMessage(messages.accurateIntegration),
    BackgroundCheckerStage: intl.formatMessage(messages.checkrIntegration),
    SterlingStage: intl.formatMessage(messages.sterlingIntegration),
  };

  let manageIntegrationHref = `${REACT_APP_MONOLITH_BASE_URL}/${accountSlug}/integrations/screening`;

  if (stage.type === 'BackgroundCheckerStage') {
    manageIntegrationHref += '?activeProvider=checkr';
  }

  return (
    <StageSettingCard
      title={cardTitleByStageType[stage.type]}
      variant="button"
      buttonText={intl.formatMessage(messages.manageIntegration)}
      buttonTextColor="primary"
      href={manageIntegrationHref}
      target="_blank"
    >
      <Grid container className={styles.cardBody} direction="column">
        <Grid container item direction="row" spacing={2}>
          {stage.type === 'BackgroundCheckerStage' && hasNodeOptions && (
            <Grid item xs={6}>
              <StyledReactSelect
                aria-label={intl.formatMessage(messages.nodeSelector)}
                options={nodeOptions}
                value={currentNodeSelection}
                onChange={onNodeChange}
                label={intl.formatMessage(messages.nodeSelector)}
                getOptionLabel={(option: NodeOption) => option.label}
                getOptionValue={(option: NodeOption) => option.value}
              />
            </Grid>
          )}
          <Grid className={styles.packageSelect} item xs={6}>
            <StyledReactSelect
              key={`package-select-${selectedNodeId ?? 'none'}`}
              aria-label={intl.formatMessage(messages.reportPackage)}
              options={availablePackages}
              value={selectedPackage}
              onChange={onPackageChange}
              label={intl.formatMessage(messages.reportPackage)}
              error={errors?.package_name?.join(', ')}
              isDisabled={isPackageSelectorDisabled}
            />
          </Grid>
        </Grid>
        {stage.type === 'BackgroundCheckerStage' && (
          <Grid container item direction="row" spacing={2}>
            <Grid className={styles.packageSelect} item xs={6}>
              <StyledReactSelect
                aria-label={intl.formatMessage(messages.checkrGeo)}
                options={stage.additional_info.checkr_geos}
                value={stage.additional_info.checkr_geo}
                onChange={onCheckrGeoChange}
                getOptionLabel={(option: CheckrGeo) =>
                  option.checkr_geo_name ?? intl.formatMessage(messages.none)
                }
                getOptionValue={(option: CheckrGeo) =>
                  option.checkr_geo_id ?? intl.formatMessage(messages.none)
                }
                label={intl.formatMessage(messages.checkrGeo)}
              />
            </Grid>
          </Grid>
        )}
        <Grid
          container
          item
          direction="column"
          className={styles.settingsContainer}
        >
          {stage.type === 'BackgroundCheckerStage' &&
            selectedPackage?.supports_mvr_first && (
              <SettingSwitch
                dataKey="mvr_first"
                checked={!!stage.additional_info.mvr_first}
                onChange={onToggleMvrFirst}
                label={intl.formatMessage(messages.progressiveScreening)}
              />
            )}
          <IsHiddenFromPortal stage={stage} setStage={setStage} />
          {hasStageLabels && (
            <AutoAdvanceWhenLabelsComplete stage={stage} setStage={setStage} />
          )}
        </Grid>
      </Grid>
    </StageSettingCard>
  );
};
